import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris';
import UserGroup from './models/UserGroup';
import Pagination from './Pagination';

export class UserGroupStore extends Pagination {
  @observable userGroups = [];

  @observable currentUserGroup = {};

  @observable showDeleteUserGroupFalsePopUP = false;

  @observable search = '';

  @observable isLoading = false;

  @observable userGroupsLength;

  @observable uerGroupsUserList = [];

  @observable userGroupListPagination = {};

  @observable defaultUserGroup = {};

  @action.bound
  setDefaultUserGroup(data) {
    this.defaultUserGroup = data;
  }

  @action
  setUserGroupsLength(num) {
    this.userGroupsLength = num;
  }

  @action resetLoading() {
    this.isLoading = false;
  }

  @action setUserGroup(data) {
    this.userGroups = data;
  }

  @action.bound
  changeCurrentUserGroup(data) {
    this.currentUserGroup = data;
  }

  @action.bound
  setSearchValue(value) {
    this.search = value;
  }

  @action.bound
  getUserGroupList(body = {}) {
    this.isLoading = true;
    axiosInstance
      .post(uris.userGroupList(), {
        ...body
      })
      .then((data) => {
        this.setPagination(data.pagination);
        this.setUserGroupListPagination(this.pagination);
        this.setUserGroup((data.userGroups || []).map(json => this.updateUserGroupFromServer(json)));
        this.setUserGroupsLength((data.userGroups || []).length);
        if (data.userGroups && data.userGroups.length) {
          this.setDefaultUserGroup(data.userGroups[0]);
        }
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  getUserGroupListWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.userGroupsLength === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getUserGroupList({
      ...data,
      ...body
    });
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
    this.getUserGroupListWithAddition();
  }

  @action.bound
  createUserGroup(userGroupName, userGroupDescription) {
    axiosInstance
      .post(
        uris.createUserGroup(),
        {
          name: userGroupName.trim(),
          description: userGroupDescription.trim()
        },
        {
          showError: {
            showPopup: true,
            title: localeText('ROLE.TITLE_ADD_FAIL')
          }
        }
      )
      .then(() => {
        message.success(localeText('USERGROUP.PRMTMSG_ADD_SUCCEED'));
        this.getUserGroupListWithAddition({forceFresh: true});
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  modifyUserGroup(data) {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.modifyUserGroup(),
        {
          id: this.currentUserGroup.id,
          name: data.userGroupNameModify.trim(),
          description: data.userGroupDescriptionModify.trim()
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_MODIFY_FAIL')
          }
        }
      )
      .then((json) => {
        this.updateUserGroupFromServer(json);
        message.success(localeText('USERGROUP.PRMTMSG_MODIFY_SUCCEED'));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateUserGroupFromServer(json) {
    let userGroup = (this.userGroups || []).find(({id}) => id === json.id);
    if (!userGroup) {
      userGroup = new UserGroup(this, json.id);
    }
    userGroup.updateFromJson(json);
    return userGroup;
  }

  @action.bound
  deleteUserGroup() {
    this.isLoading = true;
    axiosInstance
      .post(uris.deleteUserGroup(), null, {
        params: {
          id: this.currentUserGroup.id
        },
        showError: {
          showPopup: true,
          title: localeText('ROLE.TITLE_DELETE_FAIL')
        }
      })
      .then(() => {
        this.resetLoading();
        message.success(localeText('USERGROUP.PRMTMSG_DELETE_SUCCEED'));
        this.getUserGroupListWithAddition({isDelete: true});
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  checkNameExist(name, cb) {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'userGroup', name},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error);
      });
  }

  @action.bound
  getUserGroupsUserListWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.uerGroupsUserList === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getUserGroupsUserList({
      ...data,
      ...body
    });
  }

  @action.bound
  getUserGroupsUserList(body) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getUserGroupsUserList(), {
        ...body,
        userGroupId: this.currentUserGroup.id
      })
      .then((data) => {
        this.setPagination(data.pagination);
        this.setUserList(data.users);
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  setUserList(list) {
    this.userGroupsUserList = list;
  }

  @action.bound
  changeUserListPagination(pagination) {
    super.changePagination(pagination);
    this.getUserGroupsUserListWithAddition();
  }

  @action.bound
  setUserGroupListPagination(pagination) {
    this.userGroupListPagination = pagination;
  }
}

export default new UserGroupStore();
